import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../utils/AuthContext.tsx";
import ContactCard from "./ContactCard.tsx";
import ContactDetail from "./ContactDetail.tsx";
import { useBreadcrumbs } from "./BreadcrumbContext.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import AppBreadcrumb from "./AppBreadcrumb";
import { useContacts, ContactProperties } from "../utils/Api.ts";
import { useParams } from "react-router-dom";
import { Input } from "./ui/input.tsx";

const initialBreadcrumb = [
  { name: "Contacts", path: "/sources/directory" },
  { name: "All Contacts", path: "/sources/directory" },
];

function Directory() {
  const { contactId } = useParams();
  const { token, currentAccount } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const { data: contacts, isFetching: isLoadingContacts } = useContacts(currentAccount?.id!, token ?? undefined);
  const [selectedContact, setSelectedContact] = useState<string | undefined>(contactId);
  const [filter, setFilter] = useState<string>("");

  const filteredContacts = useMemo(() => {
    return contacts?.filter((c) => c.searchName.indexOf(filter.toLowerCase()) >= 0);
  }, [contacts, filter]);

  const groupedContacts = useMemo(() => {
    if (!filteredContacts) return;
    const map: Record<string, ContactProperties[]> = {};

    for (const contact of filteredContacts) {
      if (!contact) continue;
      const firstChar = !contact.displayName || contact.displayName == "" ? "_" : contact.displayName[0].toLowerCase();
      if (!map[firstChar]) {
        map[firstChar] = [];
      }
      map[firstChar].push(contact);
    }

    for (const key in map) {
      map[key].sort((a, b) => a.displayName.localeCompare(b.displayName));
    }

    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredContacts]);

  useEffect(() => {
    updateBreadcrumbs(initialBreadcrumb);
  }, []);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  return (
    <div className="w-full overflow-y-scroll">
      <div className="flex flex-row items-center pt-2 h-[72px] sm:h-[96px] pl-1">
        <AppBreadcrumb />
      </div>

      <div className="pt-2 pb-12">
        <Input placeholder="Filter" className="max-w-[300px]" onChange={handleFilterChange} value={filter} />
      </div>

      <div className="flex flex-col gap-4 pt-2 max-w-[480px] gap-y-8">
        {isLoadingContacts && (
          <>
            <div className="flex flex-col gap-2 w-[280px]">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-1/2 h-4" />
            </div>
            <div className="flex flex-col gap-2 w-[280px]">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-1/2 h-4" />
            </div>
            <div className="flex flex-col gap-2 w-[280px]">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-1/2 h-4" />
            </div>
          </>
        )}
        {groupedContacts?.map(([letter, contacts]) => (
          <div key={letter}>
            <h3 className="font-bold p-2">{letter.toUpperCase()}</h3>
            <div className="flex flex-col">
              {contacts.map((c, i) => (
                <ContactCard
                  key={i}
                  contact={c}
                  onClick={() => setSelectedContact(c.id)}
                  selected={c.id == selectedContact}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {selectedContact && <ContactDetail contactId={selectedContact} />}
    </div>
  );
}

export default Directory;
